import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from "../../Context/AuthContext";
import { COGNITO_CONFIG, isAdminHost } from "../../Utils/constants";
import { applyPostLoginRedirect, markLoginInProgress } from "../../Utils/postLoginRedirect";
import { persistCognitoTokens } from "../services/authService";
import { colors, typography } from '../../styles/theme';
import {
  authColors,
  authTitleSx,
  authPrimaryButtonSx,
  authFieldErrorSx,
} from '../styles/authPanel.styles';

const authStatusTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: typography.fontWeight.regular,
  color: authColors.textSubtitle,
  textAlign: 'center',
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const handledRef = useRef(false);

  const [error, setError] = useState("");

  useEffect(() => {
    const routerParams = new URLSearchParams(location.search);
    const windowParams = new URLSearchParams(window.location.search);

    const pick = (key) => routerParams.get(key) || windowParams.get(key);

    if (handledRef.current) return;

    const errorParam = pick("error");
    const errorDescription = pick("error_description");
    const code = pick("code");
    const state = pick("state");

    if (errorParam) {
      handledRef.current = true;
      let msg = "Error al iniciar sesión con Google";
      if (errorDescription) {
        try {
          msg = decodeURIComponent(errorDescription.replace(/\+/g, " "));
        } catch {
          msg = errorDescription;
        }
      } else {
        msg = `${msg} (${errorParam})`;
      }
      setError(msg);
      return;
    }

    if (!code) {
      handledRef.current = true;
      setError(
        "No se recibió el código de autorización. Volvé a intentar desde el inicio de sesión."
      );
      return;
    }

    handledRef.current = true;

    const storedState = sessionStorage.getItem("oauth_state");
    if (state && storedState && state !== storedState) {
      setError("Error de seguridad en la autenticación");
      sessionStorage.removeItem("oauth_state");
      return;
    }

    sessionStorage.removeItem("oauth_state");

    const exchangeToken = async () => {
      try {
        const tokenUrl = `https://${COGNITO_CONFIG.cognitoDomain}/oauth2/token`;

        const params = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: COGNITO_CONFIG.clientId,
          code,
          redirect_uri: COGNITO_CONFIG.callbackUrl,
        });

        const response = await fetch(tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        });

        if (!response.ok) {
          throw new Error("Error al obtener tokens");
        }

        const data = await response.json();

        if (!data.id_token) {
          throw new Error("No se pudo completar el inicio de sesión. Volvé a intentar.");
        }

        const { userInfo: payload } = persistCognitoTokens({
          idToken: data.id_token,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });

        await login(payload);

        window.history.replaceState({}, document.title, window.location.pathname);

        markLoginInProgress(false);
        if (applyPostLoginRedirect()) return;

        navigate(isAdminHost() ? '/admin' : '/app', { replace: true });
      } catch (err) {
        setError(err.message || "Error completando autenticación");
      }
    };

    exchangeToken();
  }, [login, navigate, location]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: '16px', sm: '24px' },
        backgroundColor: colors.backgroundDark,
        boxSizing: 'border-box',
      }}
    >
      {error ? (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '420px',
            gap: '12px',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: colors.error, mb: '4px' }} />
          <Typography
            component="h1"
            sx={{
              ...authTitleSx,
              mt: 0,
              mb: 0,
              pr: 0,
              textAlign: 'center',
            }}
          >
            No pudimos iniciar sesión
          </Typography>
          <Typography sx={{ ...authFieldErrorSx, textAlign: 'center', mb: '12px' }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => navigate('/login', { replace: true })}
            sx={{ ...authPrimaryButtonSx, minWidth: '200px' }}
          >
            Volver al login
          </Button>
        </Box>
      ) : (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '12px',
          }}
        >
          <CircularProgress size={44} thickness={4} sx={{ color: colors.primary, mb: '8px' }} />
          <Typography
            component="h1"
            sx={{
              ...authTitleSx,
              mt: 0,
              mb: 0,
              pr: 0,
              textAlign: 'center',
            }}
          >
            Ingresando a Terrio
          </Typography>
          <Typography sx={authStatusTextSx}>
            Esto solo tomará unos segundos...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AuthCallback;
